import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { Code } from 'models/common/CommonCode';
import type { EventStatus, PrjSchedule } from 'models/setup/PrjSchedule';

export const START_HOUR = 8;
export const END_HOUR = 19;
export const HOUR_HEIGHT = 56;
export const TOTAL_MINUTES = (END_HOUR - START_HOUR + 1) * 60;
export const TIMELINE_HEIGHT = (END_HOUR - START_HOUR + 1) * HOUR_HEIGHT;
export const HOURS = Array.from(
  { length: END_HOUR - START_HOUR + 1 },
  (_, idx) => START_HOUR + idx
);

export interface TimedLayoutItem {
  sch: PrjSchedule;
  startMin: number;
  endMin: number;
  col: number;
  totalCols: number;
  topPx: number;
  heightPx: number;
  leftPct: number;
  widthPct: number;
}

export interface MoreBadgeItem {
  id: string;
  startMin: number;
  endMin: number;
  topPx: number;
  leftPct: number;
  widthPct: number;
  hiddenCount: number;
  hiddenSchedules: PrjSchedule[];
  allClusterSchedules: PrjSchedule[];
}

export interface TimedLayoutResult {
  items: TimedLayoutItem[];
  moreBadges: MoreBadgeItem[];
  hasMore?: boolean;
  maxColsForDay?: number;
}

export interface TimedLayoutOptions {
  maxCols?: number; // 주 캘린더: 3, 일 캘린더: undefined(무제한)
  isExpanded?: boolean; // 전체 펼치기 모드 여부
}

export interface DragOverIndicatorInfo {
  topPx: number;
  heightPx: number;
  timeLabel: string;
  snappedTimeStr: string;
}

/**
 * 시작일과 종료일이 서로 다른 날짜인 경우에만 종일(All Day / 기간) 일정으로 판별
 */
export function isAllDayEvent(sch: PrjSchedule): boolean {
  if (!sch.planStDtm || !sch.planEndDtm) return false;
  const st = dayjs(sch.planStDtm);
  const en = dayjs(sch.planEndDtm);
  return !st.isSame(en, 'day');
}

/**
 * 당일(시작일과 종료일이 같은 날)에 진행되는 시간제 일정 판별
 */
export function isSameDayTimedEvent(sch: PrjSchedule): boolean {
  if (!sch.planStDtm || !sch.planEndDtm) return false;
  const st = dayjs(sch.planStDtm);
  const en = dayjs(sch.planEndDtm);
  return st.isSame(en, 'day');
}

export function minutesFromTimelineStart(dt: Dayjs): number {
  return dt.hour() * 60 + dt.minute() - START_HOUR * 60;
}

export function clampMinutes(value: number): number {
  return Math.max(0, Math.min(value, TOTAL_MINUTES - 15));
}

export function toHalfHourTimeFromOffset(offsetMinutes: number): string {
  const clamped = clampMinutes(offsetMinutes);
  const snapped = Math.floor(clamped / 30) * 30;
  const absolute = START_HOUR * 60 + snapped;
  const hh = String(Math.floor(absolute / 60)).padStart(2, '0');
  const mm = String(absolute % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function getDropTimeByY(clientY: number, rect: DOMRect): string {
  const y = Math.max(0, Math.min(clientY - rect.top, TIMELINE_HEIGHT));
  const minutes = Math.round((y / TIMELINE_HEIGHT) * TOTAL_MINUTES);
  return toHalfHourTimeFromOffset(minutes);
}

/**
 * 타임라인 상에서 클릭한 위치의 정각 기준 시간(HH:00) 계산
 */
export function getClickHourTimeByY(clientY: number, rect: DOMRect): string {
  const y = Math.max(0, Math.min(clientY - rect.top, TIMELINE_HEIGHT - 1));
  const hourOffset = Math.floor(y / HOUR_HEIGHT);
  const hour = Math.min(END_HOUR, Math.max(START_HOUR, START_HOUR + hourOffset));
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * 타임라인 상에서 마우스 DragOver 위치에 따른 변경 영역 점선 가이드 위치/시간 계산
 */
export function calculateTimelineDragIndicator(
  clientY: number,
  rect: DOMRect,
  durationMinutes = 60
): DragOverIndicatorInfo {
  const y = Math.max(0, Math.min(clientY - rect.top, TIMELINE_HEIGHT));
  const rawMinutes = (y / TIMELINE_HEIGHT) * TOTAL_MINUTES;
  const snappedStartMin = Math.floor(clampMinutes(rawMinutes) / 30) * 30;
  const effectiveDuration = Math.max(durationMinutes, 30);
  const endMin = Math.min(TOTAL_MINUTES, snappedStartMin + effectiveDuration);

  const topPx = (snappedStartMin / TOTAL_MINUTES) * TIMELINE_HEIGHT;
  const heightPx = Math.max(((endMin - snappedStartMin) / TOTAL_MINUTES) * TIMELINE_HEIGHT, 28);

  const startTimeStr = toHalfHourTimeFromOffset(snappedStartMin);
  const endTimeStr = toHalfHourTimeFromOffset(endMin);

  return {
    topPx,
    heightPx,
    timeLabel: `${startTimeStr} ~ ${endTimeStr}`,
    snappedTimeStr: startTimeStr,
  };
}

interface RawTimedEvent {
  sch: PrjSchedule;
  startMin: number;
  endMin: number;
}

/**
 * 당일 일정들의 시간대 겹침(Overlapping) 레이아웃 계산
 * - options.maxCols가 주어지면 (주 캘린더: 3), 최대 컬럼 수를 제한하고 초과분은 moreBadges로 반환
 * - options.maxCols가 없으면 (일 캘린더), 겹치는 만큼 컬럼 수를 무제한으로 늘려서 분할 배치
 */
export function buildTimedLayouts(
  schedules: PrjSchedule[],
  targetDay: Dayjs,
  options?: TimedLayoutOptions
): TimedLayoutResult {
  const dayStr = targetDay.format('YYYY-MM-DD');
  const maxColsLimit = options?.maxCols;
  const isExpanded = options?.isExpanded ?? false;

  const timedEvents: RawTimedEvent[] = schedules
    .filter((sch) => {
      if (!isSameDayTimedEvent(sch)) return false;
      return dayjs(sch.planStDtm).format('YYYY-MM-DD') === dayStr;
    })
    .map((sch) => {
      const st = dayjs(sch.planStDtm);
      const en = dayjs(sch.planEndDtm);

      const rawStartMin = minutesFromTimelineStart(st);
      const rawEndMin = minutesFromTimelineStart(en);

      // 타임라인 내 클램핑
      const startMin = Math.max(0, Math.min(rawStartMin, TOTAL_MINUTES - 15));
      const endMin = Math.min(TOTAL_MINUTES, Math.max(startMin + 15, rawEndMin));

      return {
        sch,
        startMin,
        endMin,
      };
    })
    .sort((a, b) => {
      if (a.startMin !== b.startMin) return a.startMin - b.startMin;
      if (a.endMin !== b.endMin) return a.endMin - b.endMin;
      return String(a.sch.schdId || '').localeCompare(String(b.sch.schdId || ''));
    });

  if (timedEvents.length === 0) {
    return { items: [], moreBadges: [], hasMore: false, maxColsForDay: 1 };
  }

  // 1. 서로 시간이 겹치는 클러스터(Cluster) 생성
  const clusters: RawTimedEvent[][] = [];
  let currentCluster: RawTimedEvent[] = [];
  let currentClusterEnd = -1;

  for (const event of timedEvents) {
    if (currentCluster.length === 0 || event.startMin < currentClusterEnd) {
      currentCluster.push(event);
      currentClusterEnd = Math.max(currentClusterEnd, event.endMin);
    } else {
      clusters.push(currentCluster);
      currentCluster = [event];
      currentClusterEnd = event.endMin;
    }
  }
  if (currentCluster.length > 0) {
    clusters.push(currentCluster);
  }

  const items: TimedLayoutItem[] = [];
  const moreBadges: MoreBadgeItem[] = [];

  let hasMore = false;
  let maxColsForDay = 1;

  // 4개 이상 겹치는 클러스터(더보기 대상)가 존재하는지 사전 확인
  if (maxColsLimit && clusters.some((cluster) => cluster.length >= 4)) {
    hasMore = true;
  }

  // 2. 각 클러스터별로 컬럼 할당
  for (const cluster of clusters) {
    // 주 캘린더 기본 모드: 4개 이상인 경우에만 2개 표시 + 3번째 컬럼에 more 버튼 배치
    if (!isExpanded && maxColsLimit && cluster.length >= 4) {
      const assignedEvents = cluster.slice(0, 2);
      const hiddenEvents = cluster.slice(2);
      const totalCols = 3;
      maxColsForDay = Math.max(maxColsForDay, totalCols);

      assignedEvents.forEach((event, idx) => {
        const topPx = (event.startMin / TOTAL_MINUTES) * TIMELINE_HEIGHT;
        const heightPx = Math.max(
          ((event.endMin - event.startMin) / TOTAL_MINUTES) * TIMELINE_HEIGHT,
          22
        );
        const leftPct = (idx / totalCols) * 100;
        const widthPct = 100 / totalCols;

        items.push({
          sch: event.sch,
          startMin: event.startMin,
          endMin: event.endMin,
          col: idx,
          totalCols,
          topPx,
          heightPx,
          leftPct,
          widthPct,
        });
      });

      const minStart = Math.min(...hiddenEvents.map((e) => e.startMin));
      const maxEnd = Math.max(...hiddenEvents.map((e) => e.endMin));
      const topPx = (minStart / TOTAL_MINUTES) * TIMELINE_HEIGHT;
      const leftPct = (2 / totalCols) * 100;
      const widthPct = 100 / totalCols;

      moreBadges.push({
        id: `more-${dayStr}-${minStart}-${hiddenEvents.length}`,
        startMin: minStart,
        endMin: maxEnd,
        topPx,
        leftPct,
        widthPct,
        hiddenCount: hiddenEvents.length,
        hiddenSchedules: hiddenEvents.map((e) => e.sch),
        allClusterSchedules: cluster.map((e) => e.sch),
      });
      continue;
    }

    // 펼치기 모드이거나, 4개 미만이거나, 제한이 없는 경우(일 캘린더 등):
    // 1줄에서 모든 일정을 가로 분할(n등분)하여 배치
    const colEnds: number[] = [];
    const assignedEvents: Array<RawTimedEvent & { col: number }> = [];

    for (const event of cluster) {
      let targetCol = -1;
      for (let c = 0; c < colEnds.length; c++) {
        if (event.startMin >= colEnds[c]) {
          targetCol = c;
          break;
        }
      }

      if (targetCol === -1) {
        targetCol = colEnds.length;
        colEnds.push(event.endMin);
      } else {
        colEnds[targetCol] = event.endMin;
      }

      assignedEvents.push({
        ...event,
        col: targetCol,
      });
    }

    const totalCols = Math.max(1, Math.max(...assignedEvents.map((e) => e.col), 0) + 1);
    maxColsForDay = Math.max(maxColsForDay, totalCols);

    for (const item of assignedEvents) {
      const topPx = (item.startMin / TOTAL_MINUTES) * TIMELINE_HEIGHT;
      const heightPx = Math.max(
        ((item.endMin - item.startMin) / TOTAL_MINUTES) * TIMELINE_HEIGHT,
        22
      );
      const leftPct = (item.col / totalCols) * 100;
      const widthPct = 100 / totalCols;

      items.push({
        sch: item.sch,
        startMin: item.startMin,
        endMin: item.endMin,
        col: item.col,
        totalCols,
        topPx,
        heightPx,
        leftPct,
        widthPct,
      });
    }
  }

  return { items, moreBadges, hasMore, maxColsForDay };
}

export interface CategoryTheme extends Code {
  id: string;
  label: string;
  bg: string;
  border: string;
  text: string;
  accent: string;
}

const PRESET_COLOR_PALETTES: Record<
  string,
  { bg: string; border: string; text: string; accent: string }
> = {
  CARRY_IN: { bg: '#E6FFFA', border: '#5EEAD4', text: '#0F766E', accent: '#0E8D80' },
  HOOKUP: { bg: '#FEF9C3', border: '#FACC15', text: '#854D0E', accent: '#A27310' },
  TURN_ON: { bg: '#F5F3FF', border: '#C4B5FD', text: '#6D28D9', accent: '#925CEF' },
  IT_TEST: { bg: '#EFF6FF', border: '#93C5FD', text: '#1D4ED8', accent: '#3F76EC' },
  ETC: { bg: '#F1F5F9', border: '#CBD5E1', text: '#475569', accent: '#64748B' },
};

const EXTRA_PALETTES = [
  { bg: '#FFF7ED', border: '#FDBA74', text: '#C2410C', accent: '#EA580C' }, // Orange
  { bg: '#FDF2F8', border: '#F472B6', text: '#BE185D', accent: '#DB2777' }, // Pink
  { bg: '#F0FDFA', border: '#5EEAD4', text: '#0F766E', accent: '#0D9488' }, // Teal
  { bg: '#F0FDF4', border: '#86EFAC', text: '#15803D', accent: '#16A34A' }, // Green
  { bg: '#F8FAFC', border: '#CBD5E1', text: '#334155', accent: '#475569' }, // Slate
];

export const EVENT_CATEGORIES: CategoryTheme[] = [
  { id: 'CARRY_IN', label: '반입', ...PRESET_COLOR_PALETTES.CARRY_IN },
  { id: 'HOOKUP', label: '훅업', ...PRESET_COLOR_PALETTES.HOOKUP },
  { id: 'TURN_ON', label: '턴온', ...PRESET_COLOR_PALETTES.TURN_ON },
  { id: 'IT_TEST', label: 'IT TEST', ...PRESET_COLOR_PALETTES.IT_TEST },
  { id: 'ETC', label: '기타', ...PRESET_COLOR_PALETTES.ETC },
];

/**
 * getCommonCodeNames('SCHD_WKT_TP_CD') 결과 목록을 CategoryTheme 배열로 변환
 */
export function buildCategoryThemes(codeList?: Array<Code>): CategoryTheme[] {
  if (!codeList || codeList.length === 0) {
    return EVENT_CATEGORIES;
  }

  return codeList.map((item, idx) => {
    const key = item.cmnCd?.toUpperCase() || '';
    const preset = PRESET_COLOR_PALETTES[key] || EXTRA_PALETTES[idx % EXTRA_PALETTES.length];
    return {
      id: item.cmnCd,
      label: item.cmnCdNm || item.cmnCd,
      ...preset,
    } as CategoryTheme;
  });
}

export function getCategoryTheme(
  category: string,
  categoryThemes: CategoryTheme[] = EVENT_CATEGORIES
): CategoryTheme {
  const found = categoryThemes.find((c) => c.id === category);
  if (found) return found;
  const etc = categoryThemes.find((c) => c.id === 'ETC');
  return etc || EVENT_CATEGORIES[4];
}

/**
 * 하위 목록(boxList / tgtEqpList)의 완료 상태 검사
 * - 반입(CARRY_IN): boxList 내의 모든 데이터가 cpltYn === 'Y' 일 때 완료
 * - 반입 이외: tgtEqpList 내의 모든 데이터가 cpltYn === 'Y' 일 때 완료
 * - 하위 목록이 비어있거나 없으면 기존 schdStatCd === 'CPLT' 여부 기준
 */
export function isScheduleCompleted(schedule: PrjSchedule): boolean {
  const category = schedule.wktTpCd || 'ETC';

  if (category === 'CARRY_IN') {
    if (Array.isArray(schedule.boxList) && schedule.boxList.length > 0) {
      return schedule.boxList.every((box) => box.cpltYn === 'Y');
    }
  } else {
    if (Array.isArray(schedule.tgtEqpList) && schedule.tgtEqpList.length > 0) {
      return schedule.tgtEqpList.every((eqp) => eqp.cpltYn === 'Y');
    }
  }

  return schedule.schdStatCd === 'CPLT';
}

/**
 * 색상 규칙:
 * 1. 완료: 완료 상태(boxList 또는 tgtEqpList의 모든 cpltYn='Y', 또는 schdStatCd='CPLT') -> 취소선 + 차분한 회색
 * 2. 지연: 종료일(planEndDtm)이 오늘 이전 && 미완료 -> border, text-color만 지연색상, bg는 업무유형색상
 * 3. 당일: 오늘 일자에 걸쳐있는 일정 (targetDate가 주어진 경우, 해당 날짜가 오늘일 때만 'today' 상태 반환)
 * 4. 일반: 업무유형 색상
 */
export function getScheduleStatus(schedule: PrjSchedule, targetDate?: string | Dayjs): EventStatus {
  const isCompleted = isScheduleCompleted(schedule);
  if (isCompleted) {
    return 'complete';
  }

  const refStr = dayjs().format('YYYY-MM-DD');
  const endDateStr = dayjs(schedule.planEndDtm).format('YYYY-MM-DD');
  const startDateStr = dayjs(schedule.planStDtm).format('YYYY-MM-DD');

  if (endDateStr < refStr) {
    return 'delayed';
  }
  if (startDateStr <= refStr && endDateStr >= refStr) {
    // 2일 이상의 기간 일정인 경우 오늘 날짜 셀에 대해서만 테두리('today') 적용
    if (targetDate) {
      const targetDateStr =
        typeof targetDate === 'string' ? targetDate : targetDate.format('YYYY-MM-DD');
      return targetDateStr === refStr ? 'today' : 'normal';
    }
    return 'today';
  }
  return 'normal';
}

export interface ScheduleStyleResult {
  bg: string;
  text: string;
  border: string;
  borderWidth: string;
  borderLeft?: string;
  borderStyle?: string;
  lineThrough: boolean;
  opacity: number;
  showDelayedBadge?: boolean;
}

export function getScheduleStyle(
  status: EventStatus,
  wktTpCd?: string,
  isDimmed = false
): ScheduleStyleResult {
  const category = wktTpCd || 'ETC';
  const catTheme = getCategoryTheme(category);
  const opacity = isDimmed ? 0.35 : 1;

  // 1. 완료 일정
  if (status === 'complete') {
    return {
      bg: '#F3F4F6',
      text: '#9CA3AF',
      border: '#D1D5DB',
      borderWidth: '1px',
      lineThrough: true,
      opacity: isDimmed ? 0.25 : 0.85,
    };
  }

  // 2. 당일 일정: "당일 일정은 현재 색상 그대로 유지"
  if (status === 'today') {
    return {
      bg: catTheme.bg,
      text: catTheme.text,
      border: '#0284C7',
      borderWidth: '2px',
      lineThrough: false,
      opacity,
    };
  }

  // 3. 지연된 일정: 1번 대안(좌측 굵은 레드 바 + 업무유형 원색 보존 + [지연] 뱃지) + 빨간색 테두리(border) 추가
  if (status === 'delayed') {
    return {
      bg: catTheme.bg,
      text: catTheme.text,
      border: catTheme.border,
      borderWidth: '1px',
      lineThrough: false,
      opacity,
      showDelayedBadge: true,
    };
  }

  // 4. 일반 일정: 업무유형 색상
  return {
    bg: catTheme.bg,
    text: catTheme.text,
    border: catTheme.border,
    borderWidth: '1px',
    lineThrough: false,
    opacity,
  };
}
